import { IMapForecastScreen } from "campaign/model"
import { IFormCoordinate } from "form/model"
import { Feature, Point, Polygon } from "geojson"
import { IBoundaryBox, ILocation } from "geometry/model"
import mapboxgl, { FillLayer, Map, PointLike, Popup, SymbolLayer } from "mapbox-gl"
import { IGeoShape, SHAPE_TYPES } from "shape/model"

export const DEFAULT_SCREENS_CLUSTER_RADIUS = 7

export const CURSOR_MOVE: string = "grabbing"
export const CURSOR_DEFAULT: string = "default"
export const CURSOR_POINTER: string = "pointer"

export const INVENTORY_POPUP_ID: string = "inventory-popup"

export const MAP_INVENTORY_CONTAINER_ID: string = "map-inventory-container"
export const MAP_GEO_TARGETS_CONTAINER_ID: string = "map-geo-targets-container"

export const BASE_MAP_LAYER_ID: string = "basemap"

export const MAPBOX_DEFAULT_LAYER_MIN_ZOOM: number = 0
export const MAPBOX_DEFAULT_LAYER_MAX_ZOOM: number = 23
export const POINT_SELECTION_CURSOR_TOLERANCE_IN_PIXELS = 2

export const MARKER_ICON_HEIGHT: number = 40

export const TEMP_COORDINATE_SOURCE_ID: string = "temp-coordinate-source-id"
export const COORDINATE_SOURCE_ID: string = "coordinate-source-id"
export const SHAPE_SOURCE_ID: string = "shape-source-id"
export const SCREENS_SOURCE_ID: string = "screens-source-id"
export const IMPRESSIONS_SOURCE_ID: string = "impressions-source-id"
export const INTERSECT_GEO_TARGET_SOURCE_ID: string = "intersect-geo-target-source-id"

export const TEMP_COORDINATE_CENTER_LAYER_ID: string = "temp-coordinate-layer-center-id"
export const TEMP_COORDINATE_CIRCLE_LAYER_ID: string = "temp-coordinate-layer-circle-id"
export const COORDINATE_CENTER_LAYER_ID: string = "coordinate-layer-center-id"
export const COORDINATE_CIRCLE_LAYER_ID: string = "coordinate-layer-circle-id"
export const INTERSECT_GEO_TARGET_FILL_LAYER_ID: string = "intersect-geo-target-fill-layer-id"
export const INTERSECT_GEO_TARGET_BORDER_LAYER_ID: string = "intersect-geo-target-border-layer-id"

export const SHAPE_POLYGON_FILL_LAYER_ID: string = "shape-layer-polygon-fill-id"
export const SHAPE_POLYGON_LINE_LAYER_ID: string = "shape-layer-polygon-line-id"
export const SCREENS_CLUSTER_TEXT_LAYER_ID: string = "screens-cluster-text-layer-id"
export const SCREENS_CLUSTER_CIRCLE_LAYER_ID: string = "screens-cluster-circle-layer-id"
export const SCREENS_POINT_LAYER_ID: string = "screens-point-layer-id"
export const IMPRESSION_LAYER_ID: string = "impression-layer-id"

export const ADD_COORDINATE_IMAGE_ID: string = "add-coordinate-image"
export const UPDATE_COORDINATE_IMAGE_ID: string = "update-coordinate-image"
export const COORDINATE_IMAGE_ID: string = "coordinate-image"
export const SCREEN_IMAGE_ID_PREFIX = "screen-image-"
export const SCREEN_IMAGE_1_ID: string = `${SCREEN_IMAGE_ID_PREFIX}1`
export const SCREEN_IMAGE_2_ID: string = `${SCREEN_IMAGE_ID_PREFIX}2`
export const SCREEN_IMAGE_3_ID: string = `${SCREEN_IMAGE_ID_PREFIX}3`
export const SCREEN_IMAGE_4_ID: string = `${SCREEN_IMAGE_ID_PREFIX}4`
export const SCREEN_IMAGE_5_ID: string = `${SCREEN_IMAGE_ID_PREFIX}5`
export const SCREEN_IMAGE_6_ID: string = `${SCREEN_IMAGE_ID_PREFIX}6`
export const SCREEN_IMAGE_7_ID: string = `${SCREEN_IMAGE_ID_PREFIX}7`
export const SCREEN_IMAGE_8_ID: string = `${SCREEN_IMAGE_ID_PREFIX}8`
export const SCREEN_IMAGE_9_ID: string = `${SCREEN_IMAGE_ID_PREFIX}9`
export const SCREEN_IMAGE_10_ID: string = `${SCREEN_IMAGE_ID_PREFIX}10`

export const TILE_SIZE_IN_PIXEL: number = 512
export const EARTH_CIRCUMFERENCE_IN_METERS_AT_EQUATOR: number = 40075016.686
export const METERS_PER_PX_AT_EQUATOR = EARTH_CIRCUMFERENCE_IN_METERS_AT_EQUATOR / TILE_SIZE_IN_PIXEL
export const METERS_PER_INCH = 0.0254
export const INCHES_PER_METER = 1 / METERS_PER_INCH
export const DEFAULT_OGC_WMS_DPI = 25.4 / 0.28 // 90.7142857142857 dpi
export const DEFAULT_IMPRESSION_EXTRUSION_VALUE = 10000

export const DEFAULT_MAP_POPUP_WIDTH_IN_PX: number = 1100
export const DEFAULT_MAP_POPUP_HEIGHT_IN_PX: number = 950

export const INVENTORY_PITCH: number = 30

export enum RASTER_SCHEME_TYPES {
  TMS = "tms",
  XYZ = "xyz"
}

export enum DISTANCE_UNITS {
  MILLIMETERS = "millimeters",
  CENTIMETERS = "centimeters",
  METERS = "meters",
  KILOMETERS = "kilometers",
  INCHES = "inches",
  FEETS = "feet",
  YARDS = "yards",
  MILES = "miles",
  NAUTICAL_MILES = "nauticalmiles"
}
export const ALL_MAP_DISTANCE_UNITS: DISTANCE_UNITS[] = [
  DISTANCE_UNITS.MILLIMETERS,
  DISTANCE_UNITS.CENTIMETERS,
  DISTANCE_UNITS.METERS,
  DISTANCE_UNITS.KILOMETERS,
  DISTANCE_UNITS.INCHES,
  DISTANCE_UNITS.FEETS,
  DISTANCE_UNITS.YARDS,
  DISTANCE_UNITS.MILES,
  DISTANCE_UNITS.NAUTICAL_MILES
]
export function getAllDistanceUnits(): DISTANCE_UNITS[] {
  return ALL_MAP_DISTANCE_UNITS.slice()
}

export interface IMapInfo {
  center: ILocation
  zoom: number
  boundaryBox: IBoundaryBox
  bearing: number
  pitch: number
  scale: number
}

export interface IMapService {
  panTo(map: Map, center: ILocation): void
  zoomTo(map: Map, zoom: number): void
  zoomToRect(map: Map, bbox: IBoundaryBox): void
  panAndZoomTo(map: Map, center: ILocation, zoom: number): void
  fitFeaturesOnMap(map: Map, features: Feature[]): void
}

export interface IMapServiceStateMap {
  containerId: string
  map: mapboxgl.Map | null
  mapInfo: IMapInfo
  scaleControl: mapboxgl.ScaleControl | null
}

export interface IMapServiceState {
  featuresByType: {
    coordinates: Array<Feature<Point | Polygon, IFormCoordinate>>
    shapes: Array<Feature<Polygon, IShapeProperties>>
    screens: Array<Feature<Point, IMapForecastScreen>>
    impressions: Array<Feature<Polygon, IMapForecastScreen>>
    intersectGeoTarget: Feature | null
  }
  nextShapeId: number
  nextFeatureId: number
  geoTargets: IMapServiceStateMap & {
    // first feature is a point, second (optional if radius is set it exists) is a circle
    tempCoordinateFeatures: Array<Feature<Point | Polygon>> | null
    coordinateLayersReady: boolean
  }
  inventory: IMapServiceStateMap & {
    isRefreshing: boolean
    width: number
    height: number
    isMaximized: boolean
    initialPositionInPixel: ILocation
    popup: Popup
    screensStyleTextColor: any
    screensStyleCircleColor: any
    screensStyleCircleRadius: any
    screensStyleIconImage: any
    screensStyleExtrusion: any
    refreshDataOnMapOpening: boolean
    isMapOpening: boolean
    currentRequest: number
  }
}

export interface ILatitudeAndZoom {
  map: Map
  zoom?: number
  latitude?: number
}

export interface IShapeProperties {
  type: SHAPE_TYPES
  shape: Partial<IGeoShape>
  isTemp: boolean
}

export interface IAddClusterSourceLayerParams {
  layer: FillLayer | SymbolLayer
  before?: string
}

export interface IAddClusterSourceParams {
  map: Map
  sourceId: string
  layers: IAddClusterSourceLayerParams[]
  features: Feature[]
}

export interface IGetPointLikeResult {
  pointLike: PointLike | [PointLike, PointLike] | undefined
  circleFeature?: Feature<Polygon>
}

export interface IMapImage {
  name: string
  url: string
}

export interface IMapPopupOpenParams {
  location: ILocation
  html: string
  toEvalJS?: string[]
  panToLocation?: boolean
}
